import type { MerchantApiClient } from "../../types.js";
import type {
  MerchantJobStatusDto,
  MerchantOnboardingActionResult,
  MerchantOnboardingStateDto,
} from "./models.js";

interface DataEnvelope<T> {
  data: T;
  meta: {
    requestId: string;
  };
}

export interface MerchantOnboardingApi {
  getOnboarding(): Promise<MerchantOnboardingStateDto>;
  runAction(action: string, idempotencyKey: string): Promise<MerchantOnboardingActionResult>;
  startCatalogSync(
    trigger: "MANUAL" | "RETRY",
    idempotencyKey: string,
  ): Promise<{ job: MerchantJobStatusDto; queued: boolean }>;
  getJob(jobId: string): Promise<MerchantJobStatusDto>;
}

export function createMerchantOnboardingApi(client: MerchantApiClient): MerchantOnboardingApi {
  return {
    async getOnboarding() {
      return unwrap(
        await client.get<DataEnvelope<MerchantOnboardingStateDto>>("/v1/api/onboarding"),
      );
    },
    async runAction(action, idempotencyKey) {
      return unwrap(
        await client.post<DataEnvelope<MerchantOnboardingActionResult>>(
          `/v1/api/onboarding/actions/${encodeURIComponent(action)}`,
          {},
          { idempotencyKey },
        ),
      );
    },
    async startCatalogSync(trigger, idempotencyKey) {
      return unwrap(
        await client.post<DataEnvelope<{ job: MerchantJobStatusDto; queued: boolean }>>(
          "/v1/api/products/sync",
          { trigger },
          { idempotencyKey, requiresFreshToken: true },
        ),
      );
    },
    async getJob(jobId) {
      return unwrap(
        await client.get<DataEnvelope<MerchantJobStatusDto>>(
          `/v1/api/jobs/${encodeURIComponent(jobId)}`,
        ),
      );
    },
  };
}

function unwrap<T>(envelope: DataEnvelope<T>): T {
  return envelope.data;
}
